import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, assetUrl, telHref, mapHref } from "../lib/api";
import { Countdown, fmtDateTime, Lightbox } from "../lib/ui";
import { Nav, Footer, useLenis } from "../components/Chrome";
import { setMeta } from "../lib/seo";

export default function AuctionDetail() {
  useLenis();
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);
  const [lb, setLb] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/auctions/${id}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!a) return;
    const desc = (a.description || "").replace(/\s+/g, " ").trim().slice(0, 155);
    setMeta({
      title: `${a.title} · TL Auctions`,
      description: desc || `Online auction closing ${a.auction_date} — ${a.location}. Bid via TL Auctions.`,
      image: a.image ? assetUrl(a.image) : undefined,
      path: `/auction/${a.id}`,
    });
  }, [a]);

  if (notFound) {
    return (<><div className="grain" /><Nav />
      <div className="lot"><button className="lot-back" onClick={() => navigate("/")}>← All auctions</button>
        <h1 className="section-title sm">Auction not found</h1></div><Footer settings={settings} /></>);
  }
  if (!a) return (<><div className="grain" /><Nav /><div className="loading">Loading…</div></>);

  const upcoming = new Date(a.auction_date).getTime() >= Date.now();
  const cover = assetUrl(a.image);
  const gallery = (a.gallery || []).map(assetUrl).filter(Boolean);
  const captions = a.captions || [];
  const loc = locations.find((l) => l.name === a.location);
  const full = loc ? [loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(", ") : "";

  return (
    <>
      <div className="grain" />
      <Nav />
      <section className="lot" data-testid="lot-detail">
        <button className="lot-back" onClick={() => navigate("/#auctions")} data-testid="lot-back">← All auctions</button>

        <div className="lot-head">
          <div>
            <span className={"lot-status " + (upcoming ? "up" : "past")}>{upcoming ? "Upcoming" : "Sold"}</span>
            <h1 className="lot-title">{a.title}</h1>
            <div className="lot-meta">
              <span>{fmtDateTime(a.auction_date)}</span>
              <span className="sep">◆</span>
              <a href={mapHref(full || a.location)} target="_blank" rel="noopener noreferrer">{a.location} ↗</a>
            </div>
          </div>
          {upcoming && <Countdown date={a.auction_date} seconds className="lot-count" label="Auction ends in" data-testid="lot-countdown" />}
        </div>

        {cover && <div className="lot-cover" onClick={() => setLb({ src: cover, caption: a.title })}><img src={cover} alt={a.title} /></div>}

        <div className="lot-grid">
          <div className="lot-desc">
            {a.description
              ? a.description.split(/\n+/).map((p, i) => <p key={i}>{p}</p>)
              : <p className="muted">Full lot listing coming soon — call the yard for a preview appointment.</p>}
            <div className="lot-cta">
              {a.external_link && <a href={a.external_link} target="_blank" rel="noopener noreferrer" className="btn btn-amber" data-testid="lot-bidding">Bidding &amp; catalog ↗</a>}
              <button className="btn btn-ghost" onClick={() => navigate("/#contact")} data-testid="lot-ask">Ask about this sale</button>
            </div>
          </div>
          <aside>
            <div className="lot-card">
              <h4>Sale details</h4>
              <dl>
                <div><dt>When</dt><dd>{fmtDateTime(a.auction_date)}</dd></div>
                <div><dt>Where</dt><dd>{a.location}{full && <><br /><span className="muted">{full}</span></>}</dd></div>
                {loc?.phone && <div><dt>Call</dt><dd><a href={telHref(loc.phone)}>{loc.phone}</a></dd></div>}
              </dl>
              <a href={mapHref(full || a.location)} target="_blank" rel="noopener noreferrer" className="lot-map">Get directions ↗</a>
            </div>
          </aside>
        </div>

        {gallery.length > 0 && (
          <div className="lot-gallery" data-testid="lot-gallery">
            <h3 className="past-title">Photos from the lot</h3>
            <div className="lg-grid">
              {gallery.map((g, i) => (
                <button key={i} className="lg-item" onClick={() => setLb({ src: g, caption: captions[i] || "" })} aria-label={captions[i] || "View photo"} data-testid={`gallery-photo-${i}`}>
                  <img src={g} alt={captions[i] || `${a.title} — photo ${i + 1}`} loading="lazy" />
                  {captions[i] && <span className="lg-cap">{captions[i]}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
      <Lightbox src={lb?.src} caption={lb?.caption} onClose={() => setLb(null)} />
      <Footer settings={settings} />
    </>
  );
}
